import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

import { API_PATHS } from '../config/api-paths';
import { SlaThresholdsApi } from '../tokens/sla-thresholds.token';
import { SlaThresholds } from '../models/sla-thresholds.model';
import { applySlaThresholds } from '../util/task-sla';
import { extractBackendErrorMessage } from '../util/backend-error';

/**
 * REST-only `SlaThresholdsApi` (EM-1558, epic EM-1447 Story 15) —
 * `GET /services/embedded/api/platform/sla-thresholds`.
 *
 * Follows `RatingScaleService` exactly (Rule 17 — reuse the caching shape, do not invent a second
 * one): the in-flight observable is `shareReplay`-ed and the resolved value held in a signal, so N
 * callers in a session cause exactly **one** HTTP call. A failure is not cached, so a later attempt
 * can still succeed.
 *
 * On success it also publishes the windows to `core/util/task-sla`'s pure `deriveSlaClass`, which is
 * what makes the KYC and Underwriting queues colour on the SAME configured ladder without either of
 * them holding a copy of the numbers.
 *
 * **No compiled-in fallback thresholds.** On failure `thresholds()` stays null, `error()` carries the
 * backend's message, and `deriveSlaClass` degrades to the only judgement that needs no configuration
 * (breached vs not) rather than inventing a ladder (Rules 09/17).
 *
 * Rules 07 / 10 — no mock fallback, no `Mock*` sibling. Playwright intercepts with `page.route()`.
 */
@Injectable()
export class SlaThresholdService implements SlaThresholdsApi {
  private readonly http = inject(HttpClient);

  private readonly thresholdsSig = signal<SlaThresholds | null>(null);
  private readonly errorSig = signal<string | null>(null);

  /** The cached in-flight/resolved request, so N callers cause exactly one HTTP call. */
  private inFlight: Observable<SlaThresholds> | null = null;

  readonly thresholds = this.thresholdsSig.asReadonly();
  readonly error = this.errorSig.asReadonly();

  load(): Observable<SlaThresholds> {
    const loaded = this.thresholdsSig();
    if (loaded) {
      return of(loaded);
    }
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.http.get<SlaThresholds>(API_PATHS.PLATFORM_SLA_THRESHOLDS).pipe(
      tap((thresholds) => {
        this.thresholdsSig.set(thresholds);
        this.errorSig.set(null);
        // The one place the pure helper learns the ladder — see the class comment.
        applySlaThresholds({
          warningWindowMinutes: thresholds.warningWindowMinutes,
          criticalWindowMinutes: thresholds.criticalWindowMinutes,
        });
      }),
      catchError((err: HttpErrorResponse) => {
        // Rule 09 — keep the backend's own reason; the caller renders this string directly.
        this.errorSig.set(extractBackendErrorMessage(err, 'Could not load the SLA thresholds.'));
        // Allow a later retry rather than caching the failure forever.
        this.inFlight = null;
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.inFlight;
  }
}
